import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Plus, Trash2, Save } from "lucide-react";
import { toast } from "sonner";

const BLANK_FORMULA = { product_name: "", per_room: 1, required: true, min_star: 0, note: "" };

export default function AdminEquipmentFormulas() {
  const [rooms, setRooms] = useState([]);
  const [formulas, setFormulas] = useState({});  // keyed by room_type_slug
  const [activeRoom, setActiveRoom] = useState(null);

  useEffect(() => {
    api.get("/room-types").then((r) => {
      setRooms(r.data);
      if (r.data.length && !activeRoom) setActiveRoom(r.data[0].slug);
    });
    api.get("/admin/equipment-formulas").then((r) => {
      const map = {};
      (r.data || []).forEach((f) => { map[f.room_type_slug] = f.formulas || []; });
      setFormulas(map);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const current = formulas[activeRoom] || [];

  const updateCurrent = (arr) => setFormulas({ ...formulas, [activeRoom]: arr });

  const addRow = () => updateCurrent([...current, { ...BLANK_FORMULA }]);
  const updateRow = (i, k, v) => updateCurrent(current.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const removeRow = (i) => updateCurrent(current.filter((_, idx) => idx !== i));

  const save = async () => {
    if (!activeRoom) return;
    try {
      await api.put(`/admin/equipment-formulas/${activeRoom}`, {
        room_type_slug: activeRoom,
        formulas: current.map((r) => ({
          ...r,
          per_room: Number(r.per_room || 0),
          min_star: Number(r.min_star || 0),
        })),
      });
      toast.success("Formül kaydedildi");
    } catch { toast.error("Hata"); }
  };

  return (
    <div className="p-8 max-w-6xl" data-testid="admin-formulas">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="heading-serif text-3xl font-semibold text-navy">Ekipman Formülleri</h1>
          <p className="text-sm text-gray-500 mt-1">Oda tipi başına ekipman çarpanlarını özelleştirin.</p>
        </div>
        <Button onClick={save} className="bg-gold hover:bg-[#c49d58]" data-testid="admin-formula-save">
          <Save size={14} className="mr-1" /> Kaydet
        </Button>
      </div>

      {/* Room tabs */}
      <div className="flex flex-wrap gap-2 mb-5">
        {rooms.map((r) => (
          <button
            key={r.slug}
            onClick={() => setActiveRoom(r.slug)}
            className={`px-4 py-2 rounded-md text-xs uppercase tracking-wider transition-colors ${
              activeRoom === r.slug ? "bg-navy text-white" : "bg-softbg text-gray-600 hover:bg-navy/10"
            }`}
            data-testid={`formula-room-${r.slug}`}
          >
            {r.name_tr}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="heading-serif font-semibold text-navy">
            {rooms.find((r) => r.slug === activeRoom)?.name_tr || "-"} — Formüller
          </h2>
          <Button type="button" size="sm" variant="outline" onClick={addRow}><Plus size={12} className="mr-1" /> Satır Ekle</Button>
        </div>

        {current.length === 0 ? (
          <p className="text-center text-gray-500 py-6 text-sm">Henüz formül yok. "Satır Ekle" ile başlayın.</p>
        ) : (
          <div className="space-y-2">
            <div className="grid grid-cols-[2fr_0.8fr_0.8fr_0.8fr_2fr_auto] gap-2 text-[10px] uppercase tracking-wider text-gray-500 px-2">
              <div>Ürün Adı</div>
              <div>Oda Başı</div>
              <div>Zorunlu</div>
              <div>Min Yıldız</div>
              <div>Not</div>
              <div></div>
            </div>
            {current.map((row, i) => (
              <div key={i} className="grid grid-cols-[2fr_0.8fr_0.8fr_0.8fr_2fr_auto] gap-2 items-center bg-softbg rounded p-2" data-testid={`formula-row-${i}`}>
                <Input className="h-9" value={row.product_name} onChange={(e) => updateRow(i, "product_name", e.target.value)} />
                <Input className="h-9" type="number" value={row.per_room} onChange={(e) => updateRow(i, "per_room", e.target.value)} />
                <label className="flex items-center justify-center">
                  <input type="checkbox" checked={!!row.required} onChange={(e) => updateRow(i, "required", e.target.checked)} />
                </label>
                <Input className="h-9" type="number" min={0} max={5} value={row.min_star} onChange={(e) => updateRow(i, "min_star", e.target.value)} />
                <Input className="h-9" value={row.note} onChange={(e) => updateRow(i, "note", e.target.value)} />
                <button type="button" onClick={() => removeRow(i)} className="text-gray-500 hover:text-rose-600 p-1">
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
